import { BUSES } from "@/lib/audioEngine";
import { MasterMeter } from "@/components/editor/MasterMeter";
import { AutoMixCard } from "@/components/editor/AutoMixCard";

const Mini = ({ label, value, min, max, step = 0.1, onChange, testId, fmt = (v) => v.toFixed(1) }) => (
  <label className="grid grid-cols-[30px_1fr_32px] items-center gap-1 text-[10px] text-slate-400">
    <span className="font-mono-ui">{label}</span>
    <input type="range" className="mini w-full" min={min} max={max} step={step} value={value} onChange={(e) => onChange(+e.target.value)} data-testid={testId} />
    <span className="font-mono-ui text-right text-slate-500">{fmt(value)}</span>
  </label>
);

const Fader = ({ value, onChange, testId }) => (
  <div className="flex flex-col items-center gap-1">
    <input type="range" className="fader h-28" min={-60} max={12} step={0.5} value={value} onChange={(e) => onChange(+e.target.value)} onDoubleClick={() => onChange(0)} data-testid={testId} />
    <span className="font-mono-ui text-[10px] text-slate-300" data-testid={`${testId}-value`}>{value <= -60 ? "-∞" : `${value > 0 ? "+" : ""}${value.toFixed(1)}`} dB</span>
  </div>
);

const Strip = ({ bus, p, set }) => (
  <div className="w-52 shrink-0 bg-[#13151C] border border-[#2A2E3D] rounded-md p-2 flex gap-2" data-testid={`mixer-strip-${bus.id}`}>
    <div className="flex-1 space-y-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-1"><span className="w-2 h-2 rounded-full" style={{ background: bus.color }} /><span className="text-[11px] font-medium truncate">{bus.name}</span></div>
      <Mini label="LOW" value={p.low} min={-12} max={12} onChange={(v) => set({ low: v })} testId={`mixer-${bus.id}-low`} />
      <Mini label="MID" value={p.mid} min={-12} max={12} onChange={(v) => set({ mid: v })} testId={`mixer-${bus.id}-mid`} />
      <Mini label="HIGH" value={p.high} min={-12} max={12} onChange={(v) => set({ high: v })} testId={`mixer-${bus.id}-high`} />
      <Mini label="THR" value={p.threshold} min={-60} max={0} step={1} onChange={(v) => set({ threshold: v })} testId={`mixer-${bus.id}-threshold`} fmt={(v) => v.toFixed(0)} />
      <Mini label="RAT" value={p.ratio} min={1} max={20} step={0.5} onChange={(v) => set({ ratio: v })} testId={`mixer-${bus.id}-ratio`} />
      <Mini label="VERB" value={p.reverb} min={0} max={1} step={0.01} onChange={(v) => set({ reverb: v })} testId={`mixer-${bus.id}-reverb`} fmt={(v) => `${Math.round(v * 100)}%`} />
      <Mini label="PAN" value={p.pan} min={-1} max={1} step={0.01} onChange={(v) => set({ pan: v })} testId={`mixer-${bus.id}-pan`} fmt={(v) => (Math.abs(v) < 0.02 ? "C" : `${v < 0 ? "L" : "R"}${Math.round(Math.abs(v) * 100)}`)} />
      <div className="flex gap-1 pt-1">
        <button onClick={() => set({ mute: !p.mute })} data-testid={`mixer-${bus.id}-mute`} className={`flex-1 text-[10px] font-bold py-1 rounded ${p.mute ? "bg-red-500 text-white" : "bg-[#0B0C10] text-slate-400"}`}>M</button>
        <button onClick={() => set({ solo: !p.solo })} data-testid={`mixer-${bus.id}-solo`} className={`flex-1 text-[10px] font-bold py-1 rounded ${p.solo ? "bg-[#FFB800] text-[#0B0C10]" : "bg-[#0B0C10] text-slate-400"}`}>S</button>
      </div>
    </div>
    <Fader value={p.gain} onChange={(v) => set({ gain: v })} testId={`mixer-${bus.id}-fader`} />
  </div>
);

export const MixerConsole = ({ mixer, setMixer, engine, projectId, clips }) => {
  const setBus = (id) => (patch) => setMixer({ ...mixer, buses: { ...mixer.buses, [id]: { ...mixer.buses[id], ...patch } } });
  return (
    <div className="flex gap-2 p-2 overflow-x-auto h-full" data-testid="mixer-console">
      <AutoMixCard projectId={projectId} clips={clips} mixer={mixer} setMixer={setMixer} />
      {BUSES.map((b) => <Strip key={b.id} bus={b} p={mixer.buses[b.id]} set={setBus(b.id)} />)}
      <div className="w-32 shrink-0 bg-[#13151C] border border-[#FFB800]/40 rounded-md p-2 flex flex-col items-center" data-testid="mixer-master">
        <span className="text-[11px] font-semibold text-[#FFB800] mb-2">MASTER</span>
        <div className="flex gap-3 items-start"><MasterMeter engine={engine} height={112} /><Fader value={mixer.master} onChange={(v) => setMixer({ ...mixer, master: v })} testId="mixer-master-fader" /></div>
      </div>
    </div>
  );
};
